import { Adb, ADB_DEFAULT_AUTHENTICATORS, AdbDaemonTransport } from "@yume-chan/adb";
import { AdbDaemonWebUsbDeviceManager, type AdbDaemonWebUsbDevice } from "@yume-chan/adb-daemon-webusb";
import { ReadableStream as YumeReadableStream } from "@yume-chan/stream-extra";
import { readBlobArrayBuffer } from "../files/ChunkedFileReader";
import type { OperationProgress } from "../types";
import { DeviceToolError } from "../errors";
import { BrowserAdbCredentialStore } from "./BrowserAdbCredentialStore";

export class AdbWebUsbAdapter {
  private device?: AdbDaemonWebUsbDevice;
  private adb?: Adb;
  private readonly credentials = new BrowserAdbCredentialStore();

  get client(): Adb | undefined {
    return this.adb;
  }

  get rawDevice(): USBDevice | undefined {
    return this.device?.raw;
  }

  async connect(): Promise<void> {
    const manager = AdbDaemonWebUsbDeviceManager.BROWSER;
    if (!manager) throw new DeviceToolError("WEBUSB_UNSUPPORTED");
    const device = await manager.requestDevice();
    if (!device) throw new DeviceToolError("DEVICE_NOT_SELECTED");
    await this.connectSelectedDevice(device);
  }

  async connectAuthorizedDevice(rawDevice: USBDevice): Promise<void> {
    const manager = AdbDaemonWebUsbDeviceManager.BROWSER;
    if (!manager) throw new DeviceToolError("WEBUSB_UNSUPPORTED");
    const device = (await manager.getDevices()).find((candidate) => {
      const candidateRaw = candidate.raw;
      if (candidateRaw === rawDevice) return true;
      if (candidateRaw.vendorId !== rawDevice.vendorId || candidateRaw.productId !== rawDevice.productId) return false;
      if (candidateRaw.serialNumber || rawDevice.serialNumber) return candidateRaw.serialNumber === rawDevice.serialNumber;
      return candidateRaw.manufacturerName === rawDevice.manufacturerName && candidateRaw.productName === rawDevice.productName;
    });
    if (!device) throw new DeviceToolError("DEVICE_NOT_SELECTED", "ADB device không còn trong danh sách WebUSB đã cấp quyền.");
    await this.connectSelectedDevice(device);
  }

  private async connectSelectedDevice(device: AdbDaemonWebUsbDevice): Promise<void> {
    const connection = await device.connect();
    const transport = await this.authenticate(device.serial, connection);
    this.device = device;
    this.adb = new Adb(transport);
  }

  async disconnect(): Promise<void> {
    await this.adb?.close();
    this.adb = undefined;
    this.device = undefined;
  }

  isConnected(): boolean {
    return Boolean(this.adb);
  }

  async shell(command: string, signal?: AbortSignal): Promise<{ stdout: string; stderr: string; exitCode: number }> {
    if (!this.adb) throw new DeviceToolError("DEVICE_NOT_SELECTED");
    const shell = this.adb.subprocess.shellProtocol;
    if (!shell) throw new DeviceToolError("ADB_COMMAND_FAILED", "Thiết bị không hỗ trợ shell protocol.");
    const process = await shell.spawn(command, signal);
    const [stdout, stderr, exitCode] = await Promise.all([
      this.readStream(process.stdout, signal),
      this.readStream(process.stderr, signal),
      process.exited,
    ]);
    return { stdout, stderr, exitCode };
  }

  async reboot(mode?: "recovery" | "bootloader" | "fastboot" | "sideload" | ""): Promise<string> {
    if (!this.adb) throw new DeviceToolError("DEVICE_NOT_SELECTED");
    if (mode === "recovery") return this.adb.power.recovery();
    if (mode === "bootloader") return this.adb.power.bootloader();
    if (mode === "fastboot") return this.adb.power.fastboot();
    if (mode === "sideload") return this.adb.power.sideload();
    return this.adb.power.reboot();
  }

  async getProperties(): Promise<Record<string, string>> {
    const keys = ["ro.product.manufacturer", "ro.product.model", "ro.product.device", "ro.build.version.release", "ro.build.version.sdk", "ro.build.fingerprint", "ro.boot.slot_suffix"];
    const values = await Promise.all(keys.map(async (key) => [key, await this.adb?.getProp(key) ?? ""] as const));
    return Object.fromEntries(values);
  }

  async installApk(file: File, options: { replace: boolean; downgrade: boolean; grant: boolean }, signal?: AbortSignal): Promise<{ stdout: string; stderr: string; exitCode: number }> {
    if (!this.adb) throw new DeviceToolError("DEVICE_NOT_SELECTED");
    const sync = await this.adb.sync();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const remote = `/data/local/tmp/usb-workbench-${safeName}`;
    try {
      await sync.write({ filename: remote, file: fileToYumeStream(file) });
      const flags = [options.replace ? "-r" : "", options.downgrade ? "-d" : "", options.grant ? "-g" : ""].filter(Boolean).join(" ");
      const result = await this.shell(`pm install ${flags} ${remote}`.trim(), signal);
      if (result.exitCode !== 0 || !/success/i.test(result.stdout)) throw new DeviceToolError("ADB_COMMAND_FAILED", `${result.stderr || result.stdout} (exit ${result.exitCode})`);
      return result;
    } finally {
      await this.adb.rm(remote, { force: true }).catch(() => undefined);
      await sync.dispose().catch(() => undefined);
    }
  }

  async push(file: File, remotePath: string, onProgress?: (completed: number) => void, signal?: AbortSignal): Promise<void> {
    if (!this.adb) throw new DeviceToolError("DEVICE_NOT_SELECTED");
    const sync = await this.adb.sync();
    let sent = 0;
    const stream = new YumeReadableStream<Uint8Array>({
      async pull(controller) {
        if (signal?.aborted) return controller.error(new DOMException("Operation cancelled", "AbortError"));
        const chunk = await file.slice(sent, Math.min(sent + 1024 * 1024, file.size)).arrayBuffer();
        if (chunk.byteLength === 0) return controller.close();
        sent += chunk.byteLength;
        onProgress?.(sent);
        controller.enqueue(new Uint8Array(chunk));
      },
    });
    try {
      await sync.write({ filename: remotePath, file: stream });
    } finally {
      await sync.dispose().catch(() => undefined);
    }
  }

  async pull(remotePath: string, suggestedName: string, onProgress?: (completed: number) => void, signal?: AbortSignal): Promise<void> {
    if (!this.adb) throw new DeviceToolError("DEVICE_NOT_SELECTED");
    if (!window.showSaveFilePicker) throw new DeviceToolError("UNSUPPORTED_IMAGE", "Trình duyệt không hỗ trợ ghi stream ra file. Hãy dùng Chrome/Edge desktop.");
    const handle = await window.showSaveFilePicker({ suggestedName: suggestedName.replace(/[^a-zA-Z0-9._-]/g, "_") });
    const writable = await handle.createWritable();
    const sync = await this.adb.sync();
    const reader = sync.read(remotePath).getReader();
    let completed = 0;
    try {
      for (;;) {
        if (signal?.aborted) throw new DeviceToolError("OPERATION_CANCELLED");
        const { done, value } = await reader.read();
        if (done) break;
        await writable.write(value);
        completed += value.byteLength;
        onProgress?.(completed);
      }
      await writable.close();
    } catch (error) {
      await writable.abort(error).catch(() => undefined);
      throw error;
    } finally {
      reader.releaseLock();
      await sync.dispose().catch(() => undefined);
    }
  }

  async sideload(file: File, onProgress?: (progress: OperationProgress) => void, signal?: AbortSignal): Promise<void> {
    if (!this.adb) throw new DeviceToolError("DEVICE_NOT_SELECTED");
    const blockSize = 64 * 1024;
    let socket;
    try {
      socket = await this.adb.createSocket(`sideload-host:${file.size}:${blockSize}`);
    } catch (error) {
      throw new DeviceToolError("ADB_COMMAND_FAILED", "Recovery không hỗ trợ sideload-host hoặc chưa ở màn hình Apply update from ADB.", undefined, error);
    }
    const reader = socket.readable.getReader();
    const writer = socket.writable.getWriter();
    const startedAt = Date.now();
    let requestedBytes = 0;
    try {
      for (;;) {
        if (signal?.aborted) throw new DeviceToolError("OPERATION_CANCELLED");
        const request = await readExactly(reader, 8, signal);
        const requestText = new TextDecoder().decode(request);
        if (requestText === "DONEDONE") break;
        if (!/^\d{8}$/.test(requestText)) throw new DeviceToolError("ADB_COMMAND_FAILED", `Yêu cầu sideload không hợp lệ: ${requestText}`);
        const block = Number.parseInt(requestText, 10);
        const offset = block * blockSize;
        if (offset >= file.size) throw new DeviceToolError("ADB_COMMAND_FAILED", "Recovery yêu cầu block vượt quá kích thước OTA.");
        const chunk = new Uint8Array(await readBlobArrayBuffer(file.slice(offset, Math.min(offset + blockSize, file.size))));
        await writer.write(chunk);
        requestedBytes += chunk.byteLength;
        onProgress?.({ phase: "Recovery đang yêu cầu dữ liệu", completedBytes: requestedBytes, totalBytes: Math.max(file.size, Math.round(file.size * 2.13)), startedAt, speedBytesPerSecond: requestedBytes / Math.max(0.001, (Date.now() - startedAt) / 1000) });
      }
    } finally {
      reader.releaseLock();
      writer.releaseLock();
      await Promise.resolve(socket.close()).catch(() => undefined);
    }
  }

  async cancelCurrentOperation(): Promise<void> {
    // The package exposes process.kill() for shell cancellation; connection closure is the safe fallback for USB I/O.
    await this.adb?.close();
  }

  private async authenticate(serial: string, connection: Parameters<typeof AdbDaemonTransport.authenticate>[0]["connection"]) {
    try {
      return await AdbDaemonTransport.authenticate({
        serial,
        connection,
        credentialStore: this.credentials,
        authenticators: ADB_DEFAULT_AUTHENTICATORS,
        initialDelayedAckBytes: 32 * 1024 * 1024,
        readTimeLimit: 30_000,
      });
    } catch (error) {
      await connection.readable.cancel().catch(() => undefined);
      await connection.writable.close().catch(() => undefined);
      if (error instanceof Error && /auth|no authenticator|unauthorized/i.test(error.message)) {
        throw new DeviceToolError("ADB_AUTH_REQUIRED", error.message, "Mở khóa thiết bị và chấp nhận RSA rồi thử lại.", error);
      }
      throw new DeviceToolError("ADB_COMMAND_FAILED", error instanceof Error ? error.message : String(error), undefined, error);
    }
  }

  private async readStream(stream: ReadableStream<Uint8Array>, signal?: AbortSignal): Promise<string> {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let output = "";
    try {
      for (;;) {
        if (signal?.aborted) throw new DeviceToolError("OPERATION_CANCELLED");
        const { done, value } = await reader.read();
        if (done) break;
        output += decoder.decode(value, { stream: true });
        if (output.length > 1024 * 1024) output = `${output.slice(0, 1024 * 1024)}\n[log bị cắt ở 1 MiB]`;
      }
      return `${output}${decoder.decode()}`;
    } finally {
      reader.releaseLock();
    }
  }
}

async function readExactly(reader: ReadableStreamDefaultReader<Uint8Array>, length: number, signal?: AbortSignal): Promise<Uint8Array> {
  const result = new Uint8Array(length);
  let offset = 0;
  while (offset < length) {
    if (signal?.aborted) throw new DeviceToolError("OPERATION_CANCELLED");
    const { done, value } = await reader.read();
    if (done || !value) throw new DeviceToolError("DEVICE_DISCONNECTED", "Sideload socket đã đóng trước khi recovery hoàn tất.");
    const count = Math.min(value.byteLength, length - offset);
    result.set(value.subarray(0, count), offset);
    offset += count;
  }
  return result;
}

function fileToYumeStream(file: File) {
  let offset = 0;
  return new YumeReadableStream<Uint8Array>({
    async pull(controller) {
      const chunk = await file.slice(offset, Math.min(offset + 1024 * 1024, file.size)).arrayBuffer();
      if (!chunk.byteLength) return controller.close();
      offset += chunk.byteLength;
      controller.enqueue(new Uint8Array(chunk));
    },
  });
}
